import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import CollapseHeader from "../../../../core/common/collapse-header";
import { ArrowLeft, Info } from "react-feather";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { API_BASE_URL } from "../../../../environment";
import { formatCustomDateTime } from "../../../../utils/dateUtils";
import { AuthContext } from "../../../../context/authcontext";
import TablePagination from "../../../../components/pagination/TablePagination";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import SortableHeader from "../../../../components/filters/SortableHeader";
import styled from "styled-components";
const route = all_routes;

interface Attachment {
    attachment_url: string;
    name: string;
    extension: string;
    entity_id: number;
    entity_name: string;
    size: string;
    created_by: string;
    created_at: string;
    related_entity_id?: number;
    related_entity_name?: string;
    external_chat_end_user_id?: number;
    is_deleted?: boolean;
}

interface StorageData {
    total_tenant_storage: string;
    total_tenant_used_storage: string;
    attachment_size: string;
    note_attachment_size: string;
    document_attachment_size: string;
    mail_attachment_size: string;
    chat_attachment_size: string;
    count?: number;
    next?: string | null;
    previous?: string | null;
    attachments: Attachment[];
}

interface TenantUser {
    tenant_user_id: number;
    user_id: number;
    user_first_name: string;
    user_last_name: string;
    avatar_url: string;
}

const ENTITY_ROUTES: Record<string, string> = {
    lead: "/leads",
    deal: "/deals",
    contact: "/contacts",
    pipeline: "/pipelines",
    company: "/companies",
    proposal: "/proposals",
    contract: "/contracts",
};

const StyledWrapper = styled.div`
  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 24px;
  }

  .toggle-switch .toggle-input {
    display: none;
  }

  .toggle-switch .toggle-label {
    position: absolute;
    top: 0;
    left: 0;
    width: 40px;
    height: 24px;
    background-color: #824EEB;
    border-radius: 34px;
    cursor: pointer;
    transition: background-color 0.3s;
  }

  .toggle-switch .toggle-label::before {
    content: "";
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    top: 2px;
    left: 2px;
    background-color: #fff;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s;
  }

  .toggle-switch .toggle-input:checked + .toggle-label {
    background-color: #d84011ff;
  }

  .toggle-switch .toggle-input:checked + .toggle-label::before {
    transform: translateX(16px);
  }

  .toggle-switch.light .toggle-label {
    background-color: #BEBEBE;
  }

  .toggle-switch.light .toggle-input:checked + .toggle-label {
    background-color: #9B9B9B;
  }

  .toggle-switch.light .toggle-input:checked + .toggle-label::before {
    transform: translateX(6px);
  }

  .toggle-switch.dark .toggle-label {
    background-color: #4B4B4B;
  }

  .toggle-switch.dark .toggle-input:checked + .toggle-label {
    background-color: #717171;
  }

  .toggle-switch.dark .toggle-input:checked + .toggle-label::before {
    transform: translateX(16px);
  }`;

const CustomSwitch = ({ checked, onChange }: { checked: boolean; onChange: (val: boolean) => void }) => {
    return (
        <StyledWrapper>
            <div className="toggle-switch">
                <input
                    className="toggle-input"
                    id="toggle_deleted"
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                />
                <label className="toggle-label" htmlFor="toggle_deleted" />
            </div>
        </StyledWrapper>
    );
};

const getEntityRoute = (entity_name: string | null | undefined, id?: number): string | null => {
    if (!id || !entity_name) return null;
    const normalized = entity_name.toLowerCase().replace(/_/g, "");
    for (const key of Object.keys(ENTITY_ROUTES)) {
        if (normalized.includes(key)) {
            return `${ENTITY_ROUTES[key]}/${id}`;
        }
    }
    return null;
};

const getRelatedEntityProps = (att: Attachment, navigate: ReturnType<typeof useNavigate>): { href?: string; label: string; onClick?: (e: React.MouseEvent) => void } | null => {
    if (att.entity_name === "mail_message") {
        if (!att.entity_id) return null;
        return {
            href: `/application/email/inbox/${att.entity_id}`,
            label: "Mail",
        };
    }
    if (att.entity_name === "external_chat_message") {
        if (!att.external_chat_end_user_id) return null;
        return {
            label: "Chat",
            onClick: (e) => {
                e.preventDefault();
                localStorage.setItem("openConversationId", String(att.external_chat_end_user_id));
                window.open(all_routes.chat, "_blank");
            },
        };
    }
    const route = getEntityRoute(att.related_entity_name || att.entity_name, att.related_entity_id);
    if (!route) return null;
    const nameToSplit = att.related_entity_name || att.entity_name || "";
    const label = nameToSplit
        .split("_")
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    return { href: route, label };
};

const getFileNameFromUrl = (url: string | null | undefined): string => {
    if (!url) return "Unknown File";
    const parts = url.split("/");
    return parts[parts.length - 1] || url;
};

const getDisplayName = (att: Attachment): string => {
    if (att.entity_name === "external_chat_message" || att.entity_name === "mail_message") {
        return getFileNameFromUrl(att.attachment_url);
    }
    return att.name || getFileNameFromUrl(att.attachment_url);
};

const formatBytes = (bytes: number): string => {
    if (isNaN(bytes) || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const truncate = (str: string, max: number): string => {
    if (str.length <= max) return str;
    return str.slice(0, max) + "…";
};

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"];
const VIDEO_EXTS = ["mp4", "webm", "ogg", "mov", "avi", "mkv"];
const DOC_EXTS = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"];

const TYPE_OPTIONS = [
    { label: "Documents", apiValue: "document", exts: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"] },
    { label: "Images", apiValue: "image", exts: ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"] },
    { label: "Videos", apiValue: "video", exts: ["mp4", "webm", "ogg", "mov", "avi", "mkv"] },
];

const SOURCE_OPTIONS = [
    { value: "note_attachment", label: "Note Attachment" },
    { value: "document_attachment", label: "Document" },
    { value: "mail_attachment", label: "Mail Attachment" },
    { value: "external_chat_message", label: "Chat Attachment" },
    { value: "attachment", label: "Attachment" },
];

const SOURCE_ENTITY_MAP: Record<string, string> = {
    note_attachment: "note",
    document_attachment: "document",
    mail_attachment: "mail_message",
    external_chat_message: "external_chat_message",
    attachment: "attachment",
};

const getFileIcon = (extension: string | null | undefined) => {
    const ext = (extension || "").toLowerCase();
    if (IMAGE_EXTS.includes(ext)) return <i className="ti ti-photo text-success me-2" style={{ fontSize: "18px" }} />;
    if (VIDEO_EXTS.includes(ext)) return <i className="ti ti-video text-danger me-2" style={{ fontSize: "18px" }} />;
    if (ext === "pdf") return <i className="ti ti-file-type-pdf text-danger me-2" style={{ fontSize: "18px" }} />;
    if (["doc", "docx"].includes(ext)) return <i className="ti ti-file-type-doc text-primary me-2" style={{ fontSize: "18px" }} />;
    if (["xls", "xlsx"].includes(ext)) return <i className="ti ti-file-type-xls text-success me-2" style={{ fontSize: "18px" }} />;
    return <i className="fa fa-file text-secondary me-2" style={{ fontSize: "18px" }} />;
};

const MediaModal = ({ attachment, onClose }: { attachment: Attachment | null; onClose: () => void }) => {
    const [mediaLoaded, setMediaLoaded] = useState(false);

    useEffect(() => {
        setMediaLoaded(false);
    }, [attachment]);

    if (!attachment) return null;

    const ext = (attachment.extension || "").toLowerCase();
    const isImage = IMAGE_EXTS.includes(ext);
    const isVideo = VIDEO_EXTS.includes(ext);
    const isDoc = DOC_EXTS.includes(ext);
    const displayName = getDisplayName(attachment);
    const truncatedName = truncate(displayName, 60);

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.75)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            onClick={onClose}
        >
            <div
                className="bg-white rounded shadow-lg"
                style={{ maxWidth: "90vw", maxHeight: "90vh", overflow: "auto", position: "relative" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                    <span className="fw-semibold" title={displayName} style={{ maxWidth: "60vw" }}>
                        {truncatedName}
                    </span>
                    <div className="d-flex align-items-center gap-2 ms-2">
                        <a
                            href={attachment.attachment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                            title="Open in new window"
                        >
                            <i className="ti ti-external-link" />
                        </a>
                        <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                            <i className="ti ti-x" />
                        </button>
                    </div>
                </div>

                <div
                    className="p-3 d-flex align-items-center justify-content-center"
                    style={{ minWidth: "320px", minHeight: "220px", position: "relative" }}
                >
                    {!mediaLoaded && (isImage || isVideo || isDoc) && (
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{ position: "absolute", inset: 0, background: "#fff", zIndex: 2 }}
                        >
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    {isImage && (
                        <img
                            src={attachment.attachment_url}
                            alt={displayName}
                            onLoad={() => setMediaLoaded(true)}
                            style={{ maxWidth: "80vw", maxHeight: "75vh", objectFit: "contain", display: mediaLoaded ? "block" : "none" }}
                        />
                    )}
                    {isVideo && (
                        <video
                            controls
                            onCanPlay={() => setMediaLoaded(true)}
                            style={{ maxWidth: "80vw", maxHeight: "75vh", display: mediaLoaded ? "block" : "none" }}
                            src={attachment.attachment_url}
                        />
                    )}
                    {isDoc && (
                        <iframe
                            src={attachment.attachment_url}
                            title={displayName}
                            onLoad={() => setMediaLoaded(true)}
                            style={{ width: "75vw", height: "75vh", border: "none", display: mediaLoaded ? "block" : "none" }}
                        />
                    )}
                    {!isImage && !isVideo && !isDoc && (
                        <div className="text-center p-5">
                            <i className="ti ti-file" style={{ fontSize: "64px", color: "#ccc" }} />
                            <p className="mt-3 text-muted">Preview not available</p>
                            <a href={attachment.attachment_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary mt-2">
                                Open File
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const PAGE_SIZE = 20;

const toApiDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const StoragePage = () => {
    const { selectedTenantId } = useContext(AuthContext);
    const navigate = useNavigate();

    const [storageStats, setStorageStats] = useState<Omit<StorageData, "attachments"> | null>(null);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [users, setUsers] = useState<TenantUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);

    const [filterType, setFilterType] = useState<string>("");
    const [filterSource, setFilterSource] = useState<string>("");
    const [filterUser, setFilterUser] = useState<string>("");
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [selectedRangeLabel, setSelectedRangeLabel] = useState<string>("Choose Date");

    const [currentPage, setCurrentPage] = useState(1);
    const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);

    const [sortBy, setSortBy] = useState<string>("created_at");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [isDeleted, setIsDeleted] = useState<boolean>(false);

    const initialDateRangeSettings = {
        endDate: new Date(),
        ranges: {
            "Last 30 Days": [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()],
            "Last 7 Days": [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()],
            "This Month": [new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()],
            "Today": [new Date(), new Date()],
        },
        startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
        timePicker: false,
        autoUpdateInput: false,
    };

    const buildUrl = useCallback((type: string, source: string, user: string, range: [Date | null, Date | null], del: boolean, page: number) => {
        if (!selectedTenantId) return null;
        const params = new URLSearchParams();
        params.set("tenant_id", String(selectedTenantId));
        if (type) {
            const found = TYPE_OPTIONS.find((o) => o.label === type);
            if (found) params.set("type", found.apiValue);
        }
        if (source) params.set("source", source);
        if (user) params.set("assigned_user_id", user);
        if (range[0]) params.set("start_date", toApiDate(range[0]));
        if (range[1]) params.set("end_date", toApiDate(range[1]));
        if (del) params.set("is_deleted", "true");
        else params.set("is_deleted", "false");
        
        params.set("page", String(page));
        params.set("page_size", String(PAGE_SIZE));

        return `${API_BASE_URL}/lookup/tenant-storage/?${params.toString()}`;
    }, [selectedTenantId]);

    const fetchStorage = useCallback(async (type: string, source: string, user: string, range: [Date | null, Date | null], isInitial = false, del = isDeleted, page = 1) => {
        const authHeader = localStorage.getItem("authCred");
        if (!authHeader || !selectedTenantId) return;

        const url = buildUrl(type, source, user, range, del, page);
        if (!url) return;

        if (isInitial) setLoading(true);
        else setFilterLoading(true);

        try {
            const res = await fetch(url, {
                headers: { "Content-Type": "application/json", Authorization: `Basic ${authHeader}` },
            });
            const data: StorageData = await res.json();
            const { attachments: atts, ...stats } = data;
            
            setStorageStats(stats);
            setAttachments(atts || []);
            setCurrentPage(page);
        } catch (err) {
            console.error("Error fetching storage data", err);
        } finally {
            if (isInitial) setLoading(false);
            else setFilterLoading(false);
        }
    }, [selectedTenantId, buildUrl, isDeleted]);

    useEffect(() => {
        fetchStorage("", "", "", [null, null], true);
    }, [selectedTenantId]);

    useEffect(() => {
        if (!selectedTenantId) return;
        const authHeader = localStorage.getItem("authCred");
        if (!authHeader) return;
        fetch(`${API_BASE_URL}/tenant-user/?tenant_id=${selectedTenantId}`, {
            headers: { "Content-Type": "application/json", Authorization: `Basic ${authHeader}` },
        })
            .then((res) => res.json())
            .then((data) => setUsers(data.results || []))
            .catch((err) => console.error("Error fetching users", err));
    }, [selectedTenantId]);

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setFilterType(val);
        fetchStorage(val, filterSource, filterUser, dateRange);
    };

    const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setFilterSource(val);
        fetchStorage(filterType, val, filterUser, dateRange);
    };

    const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setFilterUser(val);
        fetchStorage(filterType, filterSource, val, dateRange);
    };

    const handleDateRangeApply = (_event: any, picker: any) => {
        const start: Date = picker.startDate.toDate();
        const end: Date = picker.endDate.toDate();
        const label: string = picker.chosenLabel || "Custom Range";
        const range: [Date, Date] = [start, end];
        setDateRange(range);
        setSelectedRangeLabel(label);
        fetchStorage(filterType, filterSource, filterUser, range);
    };

    const handleDateRangeCancel = () => {
        setDateRange([null, null]);
        setSelectedRangeLabel("Choose Date");
        fetchStorage(filterType, filterSource, filterUser, [null, null]);
    };

    const handleResetFilters = () => {
        setFilterType("");
        setFilterSource("");
        setFilterUser("");
        setDateRange([null, null]);
        setSelectedRangeLabel("Choose Date");
        setIsDeleted(false);
        fetchStorage("", "", "", [null, null], true, false);
    };

    const handleSortChange = useCallback((newSortBy: string, newOrder: "asc" | "desc") => {
        setSortBy(newSortBy);
        setSortOrder(newOrder);
    }, []);

    const hasActiveFilters = !!(filterType || filterSource || filterUser || dateRange[0] || isDeleted);

    const totalStorage = storageStats ? parseInt(storageStats.total_tenant_storage) || 0 : 0;
    const usedStorage = storageStats ? parseInt(storageStats.total_tenant_used_storage) || 0 : 0;
    const noteSize = storageStats ? parseInt(storageStats.note_attachment_size) || 0 : 0;
    const documentSize = storageStats ? parseInt(storageStats.document_attachment_size) || 0 : 0;
    const mailSize = storageStats ? parseInt(storageStats.mail_attachment_size) || 0 : 0;
    const chatSize = storageStats ? parseInt(storageStats.chat_attachment_size) || 0 : 0;
    const attachmentSize = storageStats ? parseInt(storageStats.attachment_size) || 0 : 0;

    const pct = (n: number) => (totalStorage > 0 ? (n / totalStorage) * 100 : 0);

    const sortedAttachments = React.useMemo(() => {
        if (!attachments || attachments.length === 0) return [];
        return [...attachments].sort((a, b) => {
            let aValue: any = (a as any)[sortBy];
            let bValue: any = (b as any)[sortBy];

            if (sortBy === "name") {
                aValue = getDisplayName(a);
                bValue = getDisplayName(b);
            } else if (sortBy === "size") {
                aValue = parseInt(a.size || "0");
                bValue = parseInt(b.size || "0");
            } else if (sortBy === "source") {
                aValue = SOURCE_OPTIONS.find((o) => SOURCE_ENTITY_MAP[o.value] === a.entity_name)?.label || a.entity_name;
                bValue = SOURCE_OPTIONS.find((o) => SOURCE_ENTITY_MAP[o.value] === b.entity_name)?.label || b.entity_name;
            } else if (sortBy === "related_entity") {
                aValue = a.related_entity_name || a.entity_name;
                bValue = b.related_entity_name || b.entity_name;
            }

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            if (typeof aValue === "string" && typeof bValue === "string") {
                const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
                return sortOrder === "asc" ? comparison : -comparison;
            }

            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
            }

            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            const comparison = aStr.localeCompare(bStr);
            return sortOrder === "asc" ? comparison : -comparison;
        });
    }, [attachments, sortBy, sortOrder]);

    const totalCount = storageStats?.count || 0;
    const paginatedAttachments = sortedAttachments;

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchStorage(filterType, filterSource, filterUser, dateRange, false, isDeleted, page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const formatRelatedEntityLabel = (entity_name: string): string => {
        return entity_name
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h4 className="fw-semibold mb-0" style={{ fontSize: "22px" }}>
                        <i className="bi bi-arrow-left me-2" style={{ cursor: 'pointer' }} onClick={() => navigate(route.subscriptionmanagement)} />
                        Storage
                    </h4>
                    <div className="d-flex align-items-center gap-3">
                        <CollapseHeader />
                    </div>
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="mb-4">
                            <div className="d-flex align-items-baseline gap-1 mb-2">
                                <h2 className="fw-normal mb-0" style={{ fontSize: "28px" }}>
                                    {formatBytes(usedStorage)}
                                </h2>
                                <span className="text-muted" style={{ fontSize: "14px" }}>
                                    of {formatBytes(totalStorage)} used
                                </span>
                            </div>

                            <div className="progress mb-2" style={{ height: "8px", borderRadius: "4px" }}>
                                {attachmentSize > 0 && (
                                    <div className="progress-bar bg-primary" style={{ width: `${pct(attachmentSize)}%` }} title={`Attachments: ${formatBytes(attachmentSize)}`} />
                                )}
                                {noteSize > 0 && (
                                    <div className="progress-bar bg-danger" style={{ width: `${pct(noteSize)}%` }} title={`Note Attachments: ${formatBytes(noteSize)}`} />
                                )}
                                {documentSize > 0 && (
                                    <div className="progress-bar bg-success" style={{ width: `${pct(documentSize)}%` }} title={`Documents: ${formatBytes(documentSize)}`} />
                                )}
                                {mailSize > 0 && (
                                    <div className="progress-bar bg-info" style={{ width: `${pct(mailSize)}%` }} title={`Mail: ${formatBytes(mailSize)}`} />
                                )}
                                {chatSize > 0 && (
                                    <div className="progress-bar bg-warning" style={{ width: `${pct(chatSize)}%` }} title={`Chat: ${formatBytes(chatSize)}`} />
                                )}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-3 text-muted" style={{ fontSize: "12px" }}>
                                {attachmentSize > 0 && (
                                    <div className="d-flex align-items-center gap-1">
                                        <span className="rounded-circle bg-primary d-inline-block" style={{ width: "8px", height: "8px" }} />
                                        <span>Attachments ({formatBytes(attachmentSize)})</span>
                                    </div>
                                )}
                                {noteSize > 0 && (
                                    <div className="d-flex align-items-center gap-1">
                                        <span className="rounded-circle bg-danger d-inline-block" style={{ width: "8px", height: "8px" }} />
                                        <span>Notes ({formatBytes(noteSize)})</span>
                                    </div>
                                )}
                                {documentSize > 0 && (
                                    <div className="d-flex align-items-center gap-1">
                                        <span className="rounded-circle bg-success d-inline-block" style={{ width: "8px", height: "8px" }} />
                                        <span>Documents ({formatBytes(documentSize)})</span>
                                    </div>
                                )}
                                {mailSize > 0 && (
                                    <div className="d-flex align-items-center gap-1">
                                        <span className="rounded-circle bg-info d-inline-block" style={{ width: "8px", height: "8px" }} />
                                        <span>Mail ({formatBytes(mailSize)})</span>
                                    </div>
                                )}
                                {chatSize > 0 && (
                                    <div className="d-flex align-items-center gap-1">
                                        <span className="rounded-circle bg-warning d-inline-block" style={{ width: "8px", height: "8px" }} />
                                        <span>Chat ({formatBytes(chatSize)})</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="d-flex gap-3 align-items-end flex-wrap">
                                <div style={{ flex: "1 1 160px", minWidth: "160px" }}>
                                    <label style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px", display: "block" }}>
                                        Type
                                    </label>
                                    <select
                                        className="form-select"
                                        value={filterType}
                                        onChange={handleTypeChange}
                                        style={{ fontSize: "14px" }}
                                    >
                                        <option value="">All Types</option>
                                        {TYPE_OPTIONS.map((o) => (
                                            <option key={o.label} value={o.label}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ flex: "1 1 180px", minWidth: "180px" }}>
                                    <label style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px", display: "block" }}>
                                        Source
                                    </label>
                                    <select
                                        className="form-select"
                                        value={filterSource}
                                        onChange={handleSourceChange}
                                        style={{ fontSize: "14px" }}
                                    >
                                        <option value="">All Sources</option>
                                        {SOURCE_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                                    <label style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px", display: "block" }}>
                                        Owned By
                                    </label>
                                    <select
                                        className="form-select"
                                        value={filterUser}
                                        onChange={handleUserChange}
                                        style={{ fontSize: "14px" }}
                                    >
                                        <option value="">All Users</option>
                                        {users.map((u) => (
                                            <option key={u.user_id} value={String(u.user_id)}>
                                                {u.user_first_name} {u.user_last_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ flex: "1 1 250px", minWidth: "250px" }}>
                                    <label style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px", display: "block" }}>
                                        Date Range
                                    </label>
                                    <DateRangePicker
                                        initialSettings={initialDateRangeSettings}
                                        onApply={handleDateRangeApply}
                                        onCancel={handleDateRangeCancel}
                                    >
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={selectedRangeLabel}
                                            readOnly
                                            style={{ fontSize: "14px", cursor: "pointer" }}
                                        />
                                    </DateRangePicker>
                                </div>

                                <div style={{ flex: "1 1 120px", minWidth: "120px" }}>
                                    <label style={{ fontSize: "14px", fontWeight: 500, marginBottom: "17px", display: "block" }}>
                                        Deleted Entities
                                    </label>
                                    <CustomSwitch
                                        checked={isDeleted}
                                        onChange={(val) => {
                                            setIsDeleted(val);
                                            fetchStorage(filterType, filterSource, filterUser, dateRange, false, val);
                                        }}
                                    />
                                </div>

                                {hasActiveFilters && (
                                    <div>
                                        <label style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px", display: "block", visibility: "hidden" }}>
                                            &nbsp;
                                        </label>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleResetFilters}
                                            style={{ fontSize: "14px" }}
                                            title="Reset all filters"
                                        >
                                            <i className="ti ti-refresh me-1" />
                                            Reset
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="storage-table-wrapper">
                            {filterLoading ? (
                                <div className="d-flex justify-content-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table table-hover border-top mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ fontWeight: 600, fontSize: "13px" }}>
                                                        <SortableHeader
                                                            title="Name"
                                                            field="name"
                                                            currentSort={sortBy}
                                                            currentOrder={sortOrder}
                                                            onSort={handleSortChange}
                                                        />
                                                    </th>
                                                    <th style={{ fontWeight: 600, fontSize: "13px" }}>
                                                        <SortableHeader
                                                            title="Size"
                                                            field="size"
                                                            currentSort={sortBy}
                                                            currentOrder={sortOrder}
                                                            onSort={handleSortChange}
                                                        />
                                                    </th>
                                                    <th style={{ fontWeight: 600, fontSize: "13px" }}>
                                                        <SortableHeader
                                                            title="Source"
                                                            field="source"
                                                            currentSort={sortBy}
                                                            currentOrder={sortOrder}
                                                            onSort={handleSortChange}
                                                        />
                                                    </th>
                                                    <th style={{ fontWeight: 600, fontSize: "13px" }}>
                                                        <SortableHeader
                                                            title="Owned By"
                                                            field="created_by"
                                                            currentSort={sortBy}
                                                            currentOrder={sortOrder}
                                                            onSort={handleSortChange}
                                                        />
                                                    </th>
                                                    <th style={{ fontWeight: 600, fontSize: "13px" }}>
                                                        <SortableHeader
                                                            title="Related Entity"
                                                            field="related_entity"
                                                            currentSort={sortBy}
                                                            currentOrder={sortOrder}
                                                            onSort={handleSortChange}
                                                        />
                                                    </th>
                                                    <th style={{ fontWeight: 600, fontSize: "13px" }}>
                                                        <SortableHeader
                                                            title="Date"
                                                            field="created_at"
                                                            currentSort={sortBy}
                                                            currentOrder={sortOrder}
                                                            onSort={handleSortChange}
                                                        />
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedAttachments.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={6} className="text-center py-5 text-muted">
                                                            <i className="ti ti-file-search" style={{ fontSize: "48px", color: "#9ca3af", display: "block", marginBottom: "12px" }} />
                                                            No files found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    paginatedAttachments.map((att, idx) => {
                                                        const displayName = getDisplayName(att);
                                                        const ext = (att.extension || "").toLowerCase();
                                                        const canPreview = IMAGE_EXTS.includes(ext) || VIDEO_EXTS.includes(ext) || DOC_EXTS.includes(ext);
                                                        const relatedProps = getRelatedEntityProps(att, navigate);
                                                        const dateStr = att.created_at
                                                            ? formatCustomDateTime(att.created_at.replace(/:(\.?\d{3})$/, ".$1"), true)
                                                            : "—";

                                                        return (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        {getFileIcon(att.extension)}
                                                                        {canPreview ? (
                                                                            <span
                                                                                className="text-primary fw-medium"
                                                                                style={{ cursor: "pointer" }}
                                                                                title={displayName}
                                                                                onClick={() => setPreviewAttachment(att)}
                                                                            >
                                                                                {truncate(displayName, 50)}
                                                                            </span>
                                                                        ) : (
                                                                            <a
                                                                                href={att.attachment_url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="text-primary fw-medium"
                                                                                title={displayName}
                                                                            >
                                                                                {truncate(displayName, 50)}
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="text-muted">{formatBytes(parseInt(att.size))}</span>
                                                                </td>
                                                                <td>
                                                                    <span className="text-muted">
                                                                        {(() => {
                                                                            const label = SOURCE_OPTIONS.find(
                                                                                (o) => SOURCE_ENTITY_MAP[o.value] === att.entity_name
                                                                            )?.label || att.entity_name;
                                                                            return label.charAt(0).toUpperCase() + label.slice(1);
                                                                        })()}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="text-muted">{att.created_by || "—"}</span>
                                                                </td>
                                                                <td>
                                                                    {relatedProps ? (
                                                                        att.is_deleted ? (
                                                                            <span
                                                                                className="text-muted"
                                                                                title={`This ${relatedProps.label} has been deleted`}
                                                                                style={{ cursor: "default" }}
                                                                            >
                                                                                {relatedProps.label}
                                                                            </span>
                                                                        ) : (
                                                                            <a
                                                                                href={relatedProps.href || "#"}
                                                                                target={relatedProps.href ? "_blank" : undefined}
                                                                                rel={relatedProps.href ? "noopener noreferrer" : undefined}
                                                                                className="text-primary"
                                                                                onClick={relatedProps.onClick}
                                                                                style={{ cursor: "pointer" }}
                                                                            >
                                                                                {relatedProps.label}{" "}
                                                                                <i className="ti ti-external-link" style={{ fontSize: "13px" }} />
                                                                            </a>
                                                                        )
                                                                    ) : (
                                                                        <span className="text-muted">—</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className="text-muted" style={{ fontSize: "13px" }}>{dateStr}</span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {totalCount > 0 && (
                                        <div className="mt-3 mb-3">
                                            <TablePagination
                                                currentPage={currentPage}
                                                totalItems={totalCount}
                                                filteredItems={totalCount}
                                                isFiltered={hasActiveFilters}
                                                pageSize={PAGE_SIZE}
                                                onPageChange={handlePageChange}
                                                onPageSizeChange={() => { }}
                                                entityName="files"
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            {previewAttachment && (
                <MediaModal
                    attachment={previewAttachment}
                    onClose={() => setPreviewAttachment(null)}
                />
            )}
        </div>
    );
};

export default StoragePage;
